import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { createProjectImage, updateProjectImage, deleteProjectImage } from '../api/projectImagesApi'

export default function SchemeUploadManager({ projectId, projectType, floorCount, developerId }) {
  const queryClient = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [uploadingFloor, setUploadingFloor] = useState(null)
  const [editingScheme, setEditingScheme] = useState(null)
  const fileInputRef = useRef(null)

  const { data: schemesData, isLoading } = useQuery({
    queryKey: ['project-schemes', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_images')
        .select('*')
        .eq('project_id', projectId)
        .in('image_type', ['scheme', 'floor_plan'])
        .order('floor_level')
        .order('display_order')

      if (error) throw error
      return data || []
    },
  })

  const createMutation = useMutation({
    mutationFn: createProjectImage,
    onSuccess: () => {
      queryClient.invalidateQueries(['project-schemes', projectId])
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }) => updateProjectImage(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['project-schemes', projectId])
      setEditingScheme(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (imageId) => {
      const image = schemesData.find(img => img.id === imageId)
      if (image) {
        const pathMatch = image.image_url.match(/project-images\/(.+)$/)
        if (pathMatch) {
          await supabase.storage
            .from('project-images')
            .remove([pathMatch[1]])
        }
      }
      return deleteProjectImage(imageId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['project-schemes', projectId])
    },
  })

  const schemes = schemesData || []

  const generateFloorLabels = () => {
    if (projectType === 'townhouse' || !projectType) {
      return [{ value: null, label: 'Site Plan' }]
    }

    const floors = []
    const totalFloors = parseInt(floorCount) || 0

    if (totalFloors === 0) {
      return [{ value: 'Ground Floor', label: 'Ground Floor' }]
    }

    for (let i = 0; i < totalFloors; i++) {
      if (i === 0) {
        floors.push({ value: 'Ground Floor', label: 'Ground Floor' })
      } else {
        floors.push({ value: `Level ${i}`, label: `Level ${i}` })
      }
    }

    return floors
  }

  const floorLabels = generateFloorLabels()

  const getSchemeForFloor = (floorLabel) => {
    return schemes.find(s =>
      (projectType === 'townhouse' && s.image_type === 'scheme') ||
      (projectType === 'apartment' && s.image_type === 'floor_plan' && s.floor_level === floorLabel)
    )
  }

  const uploadScheme = async (file, floorLabel) => {
    if (!file || !file.type.startsWith('image/')) return

    setUploading(true)
    setUploadingFloor(floorLabel)

    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${developerId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('project-images')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('project-images')
        .getPublicUrl(fileName)

      const nextOrder = schemes.length > 0
        ? Math.max(...schemes.map(img => img.display_order)) + 1
        : 0

      await createMutation.mutateAsync({
        project_id: projectId,
        image_url: publicUrl,
        display_order: nextOrder,
        image_type: projectType === 'townhouse' ? 'scheme' : 'floor_plan',
        floor_level: projectType === 'apartment' ? floorLabel : null,
      })
    } catch (error) {
      console.error('Upload error:', error)
      alert('Failed to upload scheme. Please try again.')
    } finally {
      setUploading(false)
      setUploadingFloor(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileSelect = (e, floorLabel) => {
    const file = e.target.files[0]
    if (file) {
      uploadScheme(file, floorLabel)
    }
  }

  const handleDelete = (schemeId) => {
    if (confirm('Delete this scheme? This action cannot be undone.')) {
      deleteMutation.mutate(schemeId)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner spinner-lg text-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-secondary-900 mb-2">Development Scheme</h2>
        <p className="text-sm text-secondary-600">
          {projectType === 'townhouse' || !projectType
            ? 'Upload a single site plan showing the development layout'
            : floorCount
            ? `Upload a floor plan for each of the ${floorCount} floors in your building`
            : 'Upload floor plans for your building. Update floor count in project settings to add more floors.'}
        </p>
      </div>

      <div className="space-y-4">
        {floorLabels.map((floor) => {
          const existingScheme = getSchemeForFloor(floor.value)
          const isUploading = uploading && uploadingFloor === floor.value

          return (
            <div key={floor.value || 'site-plan'} className="card p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-secondary-900">{floor.label}</h3>
                {existingScheme && !editingScheme && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingScheme(existingScheme)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(existingScheme.id)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {editingScheme && existingScheme && editingScheme.id === existingScheme.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="label text-sm">Description</label>
                    <input
                      type="text"
                      value={editingScheme.description || ''}
                      onChange={(e) => setEditingScheme({ ...editingScheme, description: e.target.value })}
                      placeholder="Add a description..."
                      className="input w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingScheme(null)}
                      className="btn-outline px-4 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => updateMutation.mutate({
                        id: editingScheme.id,
                        updates: { description: editingScheme.description }
                      })}
                      disabled={updateMutation.isPending}
                      className="btn-primary px-4 py-2"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : existingScheme ? (
                <div>
                  <img
                    src={existingScheme.image_url}
                    alt={existingScheme.description || floor.label}
                    className="w-full rounded-lg border border-secondary-200 mb-2"
                  />
                  {existingScheme.description && (
                    <p className="text-sm text-secondary-600">{existingScheme.description}</p>
                  )}
                </div>
              ) : (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e, floor.value)}
                    className="hidden"
                    id={`scheme-upload-${floor.value || 'site'}`}
                  />
                  <label
                    htmlFor={`scheme-upload-${floor.value || 'site'}`}
                    className={`block w-full border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isUploading
                        ? 'border-primary-300 bg-primary-50'
                        : 'border-secondary-300 hover:border-primary-400 hover:bg-secondary-50'
                    }`}
                  >
                    {isUploading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mr-3"></div>
                        <span className="text-secondary-700">Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-12 h-12 text-secondary-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <p className="text-secondary-700 font-medium mb-1">Click to upload {floor.label}</p>
                        <p className="text-xs text-secondary-500">PNG, JPG or PDF up to 10MB</p>
                      </>
                    )}
                  </label>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {projectType === 'apartment' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-blue-600 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Floor Labeling</p>
              <p className="text-blue-700">
                Floor plans are automatically labeled starting from Ground Floor, then Level 1, Level 2, etc.
                If you need to add lower ground floors, please update the floor count in project settings.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
